import React, { useState } from "react";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const OUTCOMES = [["spoke", "Spoke"], ["voicemail", "Left voicemail"], ["no_answer", "No answer"], ["wrong_number", "Wrong number"]];
const btn = "rounded-full px-5 py-2.5 font-medium";

const CallOutcomeStep = ({ logId, name, onDone }) => {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!outcome) return toast.error("Choose how the call went");
    setSaving(true);
    try { await api.put(`/cell/call-log/${logId}`, { outcome, note }); toast.success(`Note saved to ${name}'s history`); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save the note"); }
    finally { setSaving(false); }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-head text-xl">How did the call with {name} go?</DialogTitle>
        <DialogDescription>Pick up the phone when it rings. Afterwards, add a quick note — it's saved to their history.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap gap-2" data-testid="cell-call-outcomes">
        {OUTCOMES.map(([k, l]) => (
          <button key={k} type="button" data-testid={`cell-call-outcome-${k}`} onClick={() => setOutcome(k)} className="rounded-full px-4 py-1.5 text-sm font-semibold border"
            style={outcome === k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>{l}</button>
        ))}
      </div>
      <textarea data-testid="cell-call-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} placeholder="Quick note (optional) — e.g. happy to join Thursday, asked about childcare"
        className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[#0B5E4E]" style={{ borderColor: "var(--sj-border)" }} />
      <DialogFooter>
        <button type="button" data-testid="cell-call-note-skip" onClick={onDone} className={`${btn} border`} style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Skip</button>
        <button type="button" data-testid="cell-call-note-save" disabled={saving} onClick={save} className={`${btn} text-white font-semibold disabled:opacity-60`} style={{ background: "var(--sj-primary)" }}>{saving ? "Saving…" : "Save note"}</button>
      </DialogFooter>
    </>
  );
};

export const CellCallButton = ({ recordId, name, label = "Call", endpoint = "/cell/call", body, testid, onDone }) => {
  const { user } = useAuth();
  const CALLBACK_KEY = `sojCallback:${user?.id || ""}`;
  const [open, setOpen] = useState(false);
  const [cb, setCb] = useState(() => localStorage.getItem(CALLBACK_KEY) || "");
  const [calling, setCalling] = useState(false);
  const [logId, setLogId] = useState("");
  const close = () => { setOpen(false); setLogId(""); };
  const start = async () => {
    if (!cb.trim()) return toast.error("Enter the number we should ring you on");
    setCalling(true);
    try {
      const { data } = await api.post(endpoint, { ...(body || { record_id: recordId }), staff_phone: cb.trim() });
      localStorage.setItem(CALLBACK_KEY, cb.trim());
      toast.success("Calling you now — pick up and we'll connect you");
      onDone && onDone();
      setLogId(data.log_id || "");
      if (!data.log_id) close();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start the call"); }
    finally { setCalling(false); }
  };
  return (
    <>
      <button type="button" data-testid={testid || `cell-call-${recordId}`} onClick={() => setOpen(true)} title={`Call ${name} through the app`}
        className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}>
        <PhoneCall className="h-3.5 w-3.5" />{label && <span>{label}</span>}
      </button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-md" data-testid="cell-call-dialog">
          {logId ? <CallOutcomeStep logId={logId} name={name} onDone={close} /> : (<>
            <DialogHeader>
              <DialogTitle className="font-head text-xl flex items-center gap-2"><PhoneCall className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Call {name}</DialogTitle>
              <DialogDescription>We'll ring your phone first, then connect you to {name}. The call goes out from the church's number, so neither of you sees the other's number.</DialogDescription>
            </DialogHeader>
            <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Your phone number (we'll ring this)</label>
            <input data-testid="cell-call-callback-input" value={cb} onChange={(e) => setCb(e.target.value)} placeholder="e.g. 07123 456789" className="w-full rounded-lg border px-3 py-2.5 outline-none focus:border-[#0B5E4E]" style={{ borderColor: "var(--sj-border)" }} />
            <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>Saved for next time on this device.</p>
            <DialogFooter>
              <button type="button" data-testid="cell-call-cancel" onClick={close} className={`${btn} border`} style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="button" data-testid="cell-call-start" disabled={calling} onClick={start} className={`${btn} px-6 text-white font-semibold hover:brightness-105 disabled:opacity-60`} style={{ background: "var(--sj-primary)" }}>{calling ? "Calling…" : "Call now"}</button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const canCellCall = (user) => !!(user?.caps?.call_members || user?.caps?.cell_plan_edit || user?.caps?.cell_approve);
